import { Select, MenuItem, FormControl, InputLabel, Skeleton, useMediaQuery, Box } from '@mui/material';
import { useStoreByName } from '../../../hooks/use-stores.hooks';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { IBookLevel, IParashaJump } from '@shared/types';

const styles = {
  skeleton: {
    borderRadius: "12px",
    height: 44,
    width: 200,
  },
  formControl: {
    minWidth: '100px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#fff',
    },
    '& .MuiSelect-select': {
      padding: '8px 10px',
    }
  },
  selectorsGroup: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  // Visually separated from the level pickers: different fill, own divider.
  parashaJump: {
    minWidth: '100px',
    marginInlineStart: '8px',
    paddingInlineStart: '16px',
    borderInlineStart: '1px solid rgba(0, 0, 0, 0.12)',
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      backgroundColor: '#eef2ff',
    },
    '& .MuiSelect-select': {
      padding: '8px 10px',
    }
  }
};

const SelectorsLevels = () => {
  const {
    selectedLevelOne,
    selectedLevelTwo,
    selectedLevelThree,
    selectedLevelFour,
    selectedLevelFive, // הוספת רמה חמישית
    setSelectedLevelOne,
    setSelectedLevelTwo,
    setSelectedLevelThree,
    setSelectedLevelFour,
    setSelectedLevelFive, // הוספת פונקציית קביעה לרמה חמישית
    levelOne,
    levelTwo,
    levelThree,
    levelFour,
    levelFive, // הוספת מערך רמה חמישית
    jumpToParasha,
    currentParasha,
    isLoading
  } = useStoreByName('booksStore');
  const { selectedBook } = useStoreByName('booksStore');
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');

  const labels = (() => {
    switch (selectedBook?.name_en) {
      case 'Torah':
        // חומש → פרק → פסוק. Parasha is a bookmark, not a level.
        return [t("humash"), t("chapter"), t("verse")];
      case 'Neviim':
      case 'Ketuvim':
        return [t("part"), t("book"), t("chapter"), t("verse")];
      case 'Mishna':
        return [t("masechet"), t("chapter"), t("Mishna"), t("paragraph")];
      case 'Siddur_Ashkenaz':
        return [t("section"), t("topic"), t("subTopic"), t("prayer/blessing"), t("sub-section")]; // הוספת תווית לרמה חמישית
      default:
        return [];
    }
  })();

  // Torah only: the parasha jump index lives on the selected chumash. Which
  // parasha is *current* is resolved in the store from chapter + verse, since
  // a straddling chapter changes parasha partway through.
  const isTorah = selectedBook?.name_en === 'Torah';
  const parshiotIndex: IParashaJump[] = isTorah ? selectedLevelOne?.parshiot ?? [] : [];

  // The jump itself lives in the store, so it also selects the opening verse
  // rather than leaving the previous verse selected under a new chapter.
  const handleJumpToParasha = (parashaId: number) => {
    const target = parshiotIndex.find((p) => p.id === parashaId);
    if (target) jumpToParasha(target);
  };

  return (
    <Box className="selectors-group levels-selectors" sx={styles.selectorsGroup}>
      {isLoading || !selectedLevelOne ?
        <>
          <Skeleton variant="rounded" sx={styles.skeleton} />
          <Skeleton variant="rounded" sx={styles.skeleton} />
          <Skeleton variant="rounded" sx={styles.skeleton} />
          <Skeleton variant="rounded" sx={styles.skeleton} />
        </>
        :
        <>
          <FormControl sx={styles.formControl} fullWidth={isMobile}>
            <InputLabel>{labels[0]}</InputLabel>
            <Select
              value={selectedLevelOne.id}
              label={labels[0]}
              inputProps={{ 'aria-label': labels[0] }}
              MenuProps={{
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
                transformOrigin: {
                  vertical: 'top',
                  horizontal: 'left',
                },
                PaperProps: {
                  sx: {
                    marginTop: '4px',
                    maxHeight: '300px'
                  }
                }
              }}
              onChange={(event) => {
                const selected = levelOne.find((item: IBookLevel) => item.id === event.target.value);
                setSelectedLevelOne(selected as IBookLevel);
              }}
            >
              {levelOne.map((item: IBookLevel) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.label.replace("משנה", "מסכת")}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {levelTwo && levelTwo.length > 0 &&
            <FormControl sx={styles.formControl} fullWidth={isMobile}>
              <InputLabel>{labels[1]}</InputLabel>
              <Select
                value={selectedLevelTwo?.id}
                label={labels[1]}
                inputProps={{ 'aria-label': labels[1] }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      marginTop: '4px',
                      maxHeight: '300px'
                    }
                  }
                }}
                onChange={(event) => {
                  const selected = levelTwo.find((item: IBookLevel) => item.id === event.target.value);
                  setSelectedLevelTwo(selected as IBookLevel);
                }}
              >
                {levelTwo.map((item: IBookLevel) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }

          {levelThree && levelThree.length > 0 &&
            <FormControl sx={styles.formControl} fullWidth={isMobile}>
              <InputLabel>{labels[2]}</InputLabel>
              <Select
                value={selectedLevelThree?.id}
                label={labels[2]}
                inputProps={{ 'aria-label': labels[2] }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      marginTop: '4px',
                      maxHeight: '300px'
                    }
                  }
                }}
                onChange={(event) => {
                  const selected = levelThree.find((item: IBookLevel) => item.id === event.target.value);
                  setSelectedLevelThree(selected as IBookLevel);
                }}
              >
                {levelThree.map((item: IBookLevel) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }

          {levelFour && levelFour.length > 0 &&
            <FormControl sx={styles.formControl} fullWidth={isMobile}>
              <InputLabel>{labels[3]}</InputLabel>
              <Select
                value={selectedLevelFour?.id}
                label={labels[3]}
                inputProps={{ 'aria-label': labels[3] }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      marginTop: '4px',
                      maxHeight: '300px'
                    }
                  }
                }}
                onChange={(event) => {
                  const selected = levelFour.find((item: IBookLevel) => item.id === event.target.value);
                  setSelectedLevelFour(selected as IBookLevel);
                }}
              >
                {levelFour.map((item: IBookLevel) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }

          {/* הוספת רמה חמישית עבור הסידור */}
          {levelFive && levelFive.length > 0 &&
            <FormControl sx={styles.formControl} fullWidth={isMobile}>
              <InputLabel>{labels[4]}</InputLabel>
              <Select
                value={selectedLevelFive?.id}
                label={labels[4]}
                inputProps={{ 'aria-label': labels[4] }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      marginTop: '4px',
                      maxHeight: '300px'
                    }
                  }
                }}
                onChange={(event) => {
                  const selected = levelFive.find((item: IBookLevel) => item.id === event.target.value);
                  setSelectedLevelFive(selected as IBookLevel);
                }}
              >
                {levelFive.map((item: IBookLevel) => (
                  <MenuItem key={item.id} value={item.id}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }

          {/* Torah: set apart from the cascade on purpose. It is a shortcut
              into the chumash/chapter/verse run, not a level of it — and it
              tracks where you are, so it doubles as the position indicator. */}
          {isTorah && parshiotIndex.length > 0 &&
            <FormControl sx={styles.parashaJump} fullWidth={isMobile}>
              <InputLabel>{t("parashah")}</InputLabel>
              <Select
                value={currentParasha?.id ?? ''}
                label={t("parashah")}
                inputProps={{ 'aria-label': t("parashah") }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      marginTop: '4px',
                      maxHeight: '300px'
                    }
                  }
                }}
                onChange={(event) => handleJumpToParasha(Number(event.target.value))}
              >
                {parshiotIndex.map((parasha) => (
                  <MenuItem key={parasha.id} value={parasha.id}>
                    {parasha.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          }
        </>}
    </Box>
  );
};

export default observer(SelectorsLevels);
